import { useEffect, useState } from "react";
import { activeCompany } from "./company";

// Stale-while-revalidate cache for GET fetchers: revisits resolve instantly from memory, then refresh in the background
const cache = new Map();
const inflight = new Map();
const FRESH_MS = 15000;
const SKIP = /\/(live|status|sync|activity|auth|presence)(\/|$|-)|\/sync-|\/status$/i;
let gen = 0;
let busy = new Set();
const emitBusy = () => window.dispatchEvent(new CustomEvent("api:busy", { detail: busy.size }));

export const clearSwr = () => { gen += 1; cache.clear(); inflight.clear(); busy = new Set(); emitBusy(); };

export const swrGet = (api, url, config = {}) => {
  if (SKIP.test(url) || config.responseType || config.params?.refresh) return api.get(url, config);
  const key = activeCompany() + "|" + url + JSON.stringify(config.params || {});
  const hit = cache.get(key);
  const load = (background) => {
    if (!inflight.has(key)) {
      const g = gen;
      if (background) { busy.add(key); emitBusy(); }
      const p = api.get(url, config).then((r) => {
        if (g !== gen) return r;  // cache was cleared (logout / a save) while this was running: don't store old data
        const old = cache.get(key);
        cache.set(key, { data: r.data, at: Date.now() });
        if (background && old && JSON.stringify(old.data) !== JSON.stringify(r.data)) window.dispatchEvent(new CustomEvent("api:fresh", { detail: key }));
        return r;
      }).finally(() => {
        if (inflight.get(key) === p) inflight.delete(key);
        if (busy.delete(key)) emitBusy();
      });
      inflight.set(key, p);
    }
    return inflight.get(key);
  };
  if (!hit) return load(false);
  if (Date.now() - hit.at > FRESH_MS) load(true).catch(() => {});
  return Promise.resolve({ data: hit.data });
};

// Hide the badge for refreshes started on another page
export const clearBusy = () => { if (busy.size) { busy = new Set(); emitBusy(); } };

// Bumps when background refreshes bring changed data; add it to a page's load effect deps
export const useApiFresh = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let t;
    const on = () => { clearTimeout(t); t = setTimeout(() => setTick((x) => x + 1), 60); };
    window.addEventListener("api:fresh", on);
    return () => { clearTimeout(t); window.removeEventListener("api:fresh", on); };
  }, []);
  return tick;
};

export const useApiBusy = () => {
  const [n, setN] = useState(busy.size);
  useEffect(() => {
    const on = (e) => setN(e.detail);
    window.addEventListener("api:busy", on);
    return () => window.removeEventListener("api:busy", on);
  }, []);
  return n > 0;
};
